import { useEffect, useRef, useState } from 'preact/hooks';
import {
  adminApi,
  ApiError,
  CAP_LABELS,
  dashboardApi,
  isPaidLevel,
  planLabel,
  reachedCap,
  type AccountInfo,
  type BillingOverview,
  type LimitPeriod,
  type MessageCap,
  type PaymentStatus,
  type UsagePeriod,
} from './dashboardApi';
import { capReachedWords, ErrorNote, fmtNum, Loading, Meter, refreshAccount, UpgradeButton } from './Dashboard';
import { PLANS, REMOVE_BADGE_ADDON, priceFor } from './pricingPlans';

// The two pages behind the account menu's "Billing" entry.
//
// Two pages rather than one: "what am I paying and when does it renew" and
// "how much of my allowance is left" get asked at different moments, often
// by different people — the person who owns the card is frequently not the
// person watching the quota — and putting them on one page buries both.
//
// Neither page invents numbers. Usage reads the same counters the server
// enforces limits against, so what it shows and what actually stops the
// bot answering can't disagree. Billing reads money facts live from the
// payment provider (server/src/billing/dodoApi.ts) rather than from a
// mirrored copy, so a refund or a card change can't leave this page
// contradicting the receipt in the customer's inbox.

const EM_DASH = '—';

/** The provider reports money in the currency's smallest unit. */
function fmtMoney(minorUnits: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(minorUnits / 100);
  } catch {
    // An unexpected currency code shouldn't blank out the amount.
    return `${(minorUnits / 100).toFixed(2)} ${currency}`;
  }
}

/** `utc` for a date the server counts in UTC (the monthly reset): shown in
 * local time it reads a day early anywhere west of UTC. */
function fmtDate(ms: number | null, { utc = false } = {}): string {
  if (!ms) return EM_DASH;
  return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', ...(utc ? { timeZone: 'UTC' } : {}) });
}

/** 'YYYY-MM' becomes 'September 2026'. The trial has one bucket, not a month. */
function periodLabel(period: string): string {
  if (period === 'trial') return 'Free trial';
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, { year: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** When the monthly message allowance next resets: the 1st, UTC, which is
 * what monthPeriod() in server/src/billing/entitlements.ts counts against.
 * Quoting a local-time date here would be wrong by up to a day. */
function nextMonthlyReset(nowMs: number): number {
  const now = new Date(nowMs);
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
}

function SubNav({ current }: { current: 'usage' | 'billing' }) {
  return (
    <nav class="db-subnav">
      <a href="/app" class="db-subnav-back">
        Back to bots
      </a>
      <span class="db-subnav-links">
        <a href="/app/usage" class={current === 'usage' ? 'db-subnav-on' : ''}>
          Usage
        </a>
        <a href="/app/billing" class={current === 'billing' ? 'db-subnav-on' : ''}>
          Billing
        </a>
      </span>
    </nav>
  );
}

function PageShell({ current, children }: { current: 'usage' | 'billing'; children: preact.ComponentChildren }) {
  return (
    <section class="site-section db-page">
      <div class="site-wrap">
        <SubNav current={current} />
        {children}
      </div>
    </section>
  );
}

/**
 * The plan, spelled out. The banner across the top of the dashboard only
 * has room for a name ("Basic plan"), which tells a customer who has just
 * paid nothing about what they bought — this is where the name is cashed
 * out into the limits that actually apply.
 */
function PlanSummary({ account }: { account: AccountInfo }) {
  const { limits } = account;
  const name = account.level === 'trial' ? 'Free trial' : `${planLabel(account.level)} plan`;
  const per = limits.messagesPer === 'month' ? 'a month' : 'for the trial';
  return (
    <div class="db-plan-summary">
      <div class="db-plan-summary-head">
        <p class="db-plan-summary-label">Current plan</p>
        <h2 class="db-plan-summary-name">{name}</h2>
      </div>
      <ul class="db-plan-summary-list">
        <li>
          {account.messageCaps ? (
            // The admin: the plan's own number means nothing; their limits do.
            <>
              Messages: <strong>your own</strong> daily, weekly and monthly limits
            </>
          ) : (
            <>
              <strong>{fmtNum(limits.messages)}</strong> messages {per}
            </>
          )}
        </li>
        <li>
          <strong>{fmtNum(limits.bots)}</strong> {limits.bots === 1 ? 'chatbot' : 'chatbots'}
        </li>
        <li>
          <strong>{fmtNum(limits.pages)}</strong> pages of content
        </li>
        <li>Custom logo and colors</li>
        <li>
          {limits.autoRefresh === 'weekly'
            ? 'Weekly auto-refresh of your site'
            : limits.autoRefresh === 'monthly'
              ? 'Monthly auto-refresh of your site'
              : 'Manual site rescan (no auto-refresh)'}
        </li>
        <li>{account.showBadge ? '"Powered by VolgaChat" shown under your chat' : 'No VolgaChat badge'}</li>
        <li>{limits.unansweredReports ? 'Unanswered-question reports' : 'No unanswered-question reports'}</li>
      </ul>
    </div>
  );
}

/** A limit field's text as the server takes it: blank is no limit. */
function limitFromField(text: string): number | null | 'invalid' {
  const clean = text.replace(/[\s,]/g, '');
  if (!clean) return null;
  return /^\d+$/.test(clean) && Number(clean) >= 1 ? Number(clean) : 'invalid';
}

/**
 * The admin's own message limits: how many messages their bots may answer
 * a day, a week and a month before they stop and offer the contact form
 * (server/src/admin/settings.ts). Their plan has no allowance of its own,
 * so without these a bot someone floods with questions would answer — and
 * cost — without end.
 */
function MessageLimitsCard({ caps, onSaved }: { caps: MessageCap[]; onSaved: () => Promise<void> }) {
  const initial = () => Object.fromEntries(caps.map((cap) => [cap.period, cap.limit === null ? '' : String(cap.limit)])) as Record<LimitPeriod, string>;
  const [fields, setFields] = useState<Record<LimitPeriod, string>>(initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save(e: Event) {
    e.preventDefault();
    const messageLimits = {} as Record<LimitPeriod, number | null>;
    for (const cap of caps) {
      const value = limitFromField(fields[cap.period]);
      if (value === 'invalid') {
        setError(`The ${CAP_LABELS[cap.period].name.toLowerCase()} limit must be a whole number of at least 1, or blank for no limit.`);
        return;
      }
      messageLimits[cap.period] = value;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await adminApi.saveSettings({ messageLimits });
      // Saved either way: a failed re-read of the page mustn't report the
      // save itself as failed.
      await onSaved().catch(() => undefined);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your limits.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form class="db-limits-card" onSubmit={save}>
      <h2 class="db-usage-h2">Message limits</h2>
      <p class="db-sub">
        How many messages your own bots may answer before they stop and offer visitors the contact form instead — so a bot someone floods with
        questions can't run up an endless bill. Days and weeks (Monday to Sunday) are UTC. You're emailed when a limit is reached. Leave a box
        blank for no limit.
      </p>
      <div class="db-limits-fields">
        {caps.map((cap) => (
          <label key={cap.period} class="db-limits-field">
            <span>{CAP_LABELS[cap.period].name}</span>
            <input
              class="db-input"
              inputMode="numeric"
              placeholder="No limit"
              value={fields[cap.period]}
              onInput={(e) => {
                setFields({ ...fields, [cap.period]: (e.target as HTMLInputElement).value });
                setError(null);
                setSaved(false);
              }}
            />
          </label>
        ))}
      </div>
      <div class="db-limits-actions">
        <button type="submit" class="site-btn site-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save limits'}
        </button>
        {saved && (
          <span class="db-muted" role="status">
            Saved — in force from the next message.
          </span>
        )}
      </div>
      <ErrorNote error={error} />
    </form>
  );
}

export function UsagePage() {
  const [data, setData] = useState<{ account: AccountInfo; history: UsagePeriod[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // After the admin changes their limits: this page, and the banner across
  // the dashboard, which reads the shared account copy.
  const load = async () => {
    setData(await dashboardApi.getUsage());
    void refreshAccount();
  };

  useEffect(() => {
    let live = true;
    dashboardApi
      .getUsage()
      .then((res) => {
        if (live) setData(res);
      })
      .catch((err) => {
        if (live) setError(err instanceof Error ? err.message : 'Could not load your usage.');
      });
    return () => {
      live = false;
    };
  }, []);

  if (error) {
    return (
      <PageShell current="usage">
        <ErrorNote error={error} />
      </PageShell>
    );
  }
  if (!data) {
    return (
      <PageShell current="usage">
        <Loading label="Loading your usage…" />
      </PageShell>
    );
  }

  const { account, history } = data;
  const { limits } = account;
  const caps = account.messageCaps;
  const monthCap = caps?.find((cap) => cap.period === 'month');
  // An admin's headline is their own monthly limit, when they have one —
  // the plan's billion is not a number anyone is counting down from.
  const monthLimit = monthCap ? monthCap.limit : limits.messages;
  const messagesLeft = monthLimit === null ? null : Math.max(0, monthLimit - account.messagesUsed);
  const capHit = caps ? reachedCap(account) : null;
  const monthly = limits.messagesPer === 'month';
  const resetLine = monthly
    ? `Resets on ${fmtDate(nextMonthlyReset(Date.now()), { utc: true })}.`
    : account.trialEndsAt
      ? `Your trial allowance runs until ${fmtDate(account.trialEndsAt)}.`
      : '';
  // Bars are relative to the busiest period, not to the limit: on a plan
  // sized for growth every bar would otherwise be a sliver.
  const peak = Math.max(1, ...history.map((h) => h.messages));

  return (
    <PageShell current="usage">
      <h1>Usage</h1>
      <p class="db-sub">What you have used of your plan, and what is left.</p>

      <PlanSummary account={account} />

      <div class="db-usage-headline">
        <p class="db-usage-big">
          {messagesLeft === null ? (
            <>
              <strong>{fmtNum(account.messagesUsed)}</strong> messages this month, no monthly limit
            </>
          ) : (
            <>
              <strong>{fmtNum(messagesLeft)}</strong> of {fmtNum(monthLimit ?? 0)} messages left{caps ? ' this month' : ''}
            </>
          )}
        </p>
        {resetLine ? <p class="db-sub">{resetLine}</p> : null}
      </div>

      {capHit && (
        <div class="db-plan-banner db-plan-warn" role="status">
          <div class="db-plan-text">
            <strong>{capReachedWords(capHit).title}</strong>
            <span>{capReachedWords(capHit).detail}</span>
          </div>
        </div>
      )}

      <div class="db-usage-meters">
        {caps ? (
          caps.map((cap) => <Meter key={cap.period} label={`Messages ${CAP_LABELS[cap.period].current.toLowerCase()}`} used={cap.used} limit={cap.limit} />)
        ) : (
          <Meter label={monthly ? 'Messages this month' : 'Messages this trial'} used={account.messagesUsed} limit={limits.messages} />
        )}
        <Meter label="Pages of content" used={account.pagesUsed} limit={limits.pages} />
        <Meter label="Chatbots" used={account.botsUsed} limit={limits.bots} />
      </div>

      {caps && <MessageLimitsCard caps={caps} onSaved={load} />}

      {!caps && messagesLeft === 0 && (
        <div class="db-plan-banner db-plan-warn" role="status">
          <div class="db-plan-text">
            <strong>You have used every message on this plan.</strong>
            <span>
              Your bot has stopped answering visitors{monthly ? ' until the allowance resets' : ''}. Upgrading switches it back on
              immediately — nothing you set up is lost.
            </span>
          </div>
          <UpgradeButton />
        </div>
      )}

      <h2 class="db-usage-h2">Message history</h2>
      {history.length === 0 ? (
        <p class="db-sub">No messages answered yet.</p>
      ) : (
        <div class="db-table-wrap">
          <table class="db-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Messages</th>
                <th class="db-usage-barcol" />
              </tr>
            </thead>
            <tbody>
              {history.map((row) => (
                <tr key={row.period}>
                  <td>{periodLabel(row.period)}</td>
                  <td>{fmtNum(row.messages)}</td>
                  <td class="db-usage-barcol">
                    <span class="db-usage-bar" style={`width:${Math.round((row.messages / peak) * 100)}%`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}

const PAYMENT_TONE: Record<string, string> = {
  succeeded: 'db-badge-ready',
  processing: 'db-badge-pending',
  requires_customer_action: 'db-badge-pending',
  failed: 'db-badge-failed',
  cancelled: 'db-badge-failed',
};

/**
 * The Remove branding add-on as a card: "Remove branding — $20/mo" while it can be
 * bought, "Active" once it is. Shared by the Billing page's Add-ons and
 * the pricing page (the account menu's "Upgrade"), so an eligible Basic
 * customer finds it wherever they go to spend more — and both places say
 * the same thing.
 */
export function RemoveBadgeCard({
  active,
  checkoutOpen,
  justPaid = false,
}: {
  active: boolean;
  checkoutOpen: boolean;
  justPaid?: boolean;
}) {
  if (active) {
    return (
      <div class="db-addon">
        <div class="db-addon-text">
          <strong>{REMOVE_BADGE_ADDON.name}</strong>
          <span>
            Active — ${REMOVE_BADGE_ADDON.monthly}/mo. Your chat shows no VolgaChat badge. You can cancel it any time on the{' '}
            <a href="/app/billing">Billing page</a>.
          </span>
        </div>
      </div>
    );
  }
  return (
    <div class="db-addon">
      <div class="db-addon-text">
        <strong>
          {REMOVE_BADGE_ADDON.name} — ${REMOVE_BADGE_ADDON.monthly}/mo
        </strong>
        <span>Your chat shows "Powered by VolgaChat" at the bottom. This removes it on every site. Cancel any time.</span>
      </div>
      <div class="db-addon-checkout">
        {justPaid ? (
          <p class="db-addon-note">
            Thanks — your payment is being confirmed. The badge disappears as soon as it is; refresh this page in a minute to check.
          </p>
        ) : checkoutOpen ? (
          <a href="/billing/checkout/addon/remove-badge" class="site-btn site-btn-primary">
            Remove branding — ${REMOVE_BADGE_ADDON.monthly}/mo
          </a>
        ) : (
          <>
            <button type="button" class="site-btn site-btn-primary" disabled>
              Remove branding — ${REMOVE_BADGE_ADDON.monthly}/mo
            </button>
            <p class="db-addon-note">Checkout for this add-on opens soon. You won't be charged anything until it does.</p>
          </>
        )}
      </div>
    </div>
  );
}

/** One id in the reference block, with a Copy button. */
function RefRow({ label, value, detail }: { label: string; value: string; detail?: string | null }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // No clipboard permission: the id is selectable text anyway.
    }
  }
  return (
    <div class="db-ref-row">
      <dt>{label}</dt>
      <dd>
        {detail && <span class="db-ref-detail">{detail}</span>}
        <code class="db-ref-id">{value}</code>
        <button type="button" class="db-btn-sm db-ref-copy" onClick={copy} aria-label={`Copy ${label.toLowerCase()} id`}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </dd>
    </div>
  );
}

/**
 * The Dodo identifiers behind the page — subscription, product, customer,
 * add-on — so a subscription can be found in the Dodo dashboard, or quoted
 * to support, without guessing which product it's on. Shown only for an
 * account that has paid through Dodo.
 */
function BillingReference({ reference }: { reference: BillingOverview['reference'] }) {
  if (!reference.subscriptionId && !reference.customerId && !reference.addonSubscriptionId) return null;
  return (
    <details class="db-ref" open>
      <summary>Payment reference</summary>
      <p class="db-sub db-ref-note">The identifiers Dodo Payments uses for your subscription — quote them if you contact support.</p>
      <dl class="db-ref-list">
        {reference.subscriptionId && <RefRow label="Subscription" value={reference.subscriptionId} />}
        {reference.productId && <RefRow label="Product" value={reference.productId} detail={reference.productLabel} />}
        {reference.customerId && <RefRow label="Customer" value={reference.customerId} />}
        {reference.addonSubscriptionId && (
          <RefRow label="Add-on subscription" value={reference.addonSubscriptionId} detail="Remove VolgaChat branding" />
        )}
      </dl>
    </details>
  );
}

/**
 * The Billing page's card for a running Remove branding add-on, with its
 * Cancel — and, once cancelled, "Keep it" until the paid month runs out.
 * Cancelling stops it at the end of that month, so there's no further
 * charge and the badge stays off until then (server/src/billing/sync.ts's
 * setAddonCancellation).
 */
function ActiveRemoveBadgeCard({
  cancelsAt,
  onChange,
}: {
  cancelsAt: number | null;
  onChange: (cancelsAt: number | null, account: AccountInfo) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(cancel: boolean) {
    setBusy(true);
    setError(null);
    try {
      const res = cancel ? await dashboardApi.cancelRemoveBadge() : await dashboardApi.resumeRemoveBadge();
      setConfirming(false);
      onChange(res.cancelsAt, res.account);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn't work — please try again.");
    } finally {
      setBusy(false);
    }
  }

  let text: preact.ComponentChildren;
  let actions: preact.ComponentChildren;
  if (cancelsAt !== null) {
    text = (
      <>
        Cancelled — you won't be charged again. Your chat stays free of the VolgaChat badge until {fmtDate(cancelsAt)}, then it comes
        back.
      </>
    );
    actions = (
      <button type="button" class="site-btn site-btn-primary" onClick={() => run(false)} disabled={busy}>
        {busy ? 'Saving…' : 'Keep it'}
      </button>
    );
  } else if (confirming) {
    text = (
      <>
        Cancel {REMOVE_BADGE_ADDON.name}? You won't be charged again. The badge stays off until the end of the month you've paid for,
        then "Powered by VolgaChat" comes back. You can change your mind until then.
      </>
    );
    actions = (
      <>
        <button type="button" class="site-btn site-btn-primary" onClick={() => run(true)} disabled={busy}>
          {busy ? 'Cancelling…' : 'Yes, cancel add-on'}
        </button>
        <button type="button" class="site-btn" onClick={() => setConfirming(false)} disabled={busy}>
          Keep it
        </button>
      </>
    );
  } else {
    text = <>Active — ${REMOVE_BADGE_ADDON.monthly}/mo. Your chat shows no VolgaChat badge.</>;
    actions = (
      <button type="button" class="site-btn" onClick={() => setConfirming(true)}>
        Cancel add-on
      </button>
    );
  }

  return (
    <div class="db-addon">
      <div class="db-addon-text">
        <strong>{REMOVE_BADGE_ADDON.name}</strong>
        <span>{text}</span>
        {error && <ErrorNote error={error} />}
      </div>
      <div class="db-addon-checkout">{actions}</div>
    </div>
  );
}

/**
 * Add-ons on top of the plan. Only one so far: removing "Powered by
 * VolgaChat" from the chat panel, $20/mo, sold on Starter — the trial
 * always shows the badge, and Professional and Business never do.
 *
 * "Add" goes to the add-on's own Dodo checkout
 * (/billing/checkout/addon/remove-badge); its webhook marks it active
 * (server/src/billing/addons.ts) and the badge goes from the chat on the
 * next page load. Until the add-on's Dodo product link is configured
 * (checkoutOpen), the button is shown disabled and says so, rather than
 * taking a payment nothing would honour.
 */
function Addons({
  account,
  checkoutOpen,
  cancelsAt,
  paymentFailed,
  onChange,
}: {
  account: AccountInfo;
  checkoutOpen: boolean;
  cancelsAt: number | null;
  paymentFailed: boolean;
  onChange: (cancelsAt: number | null, account: AccountInfo) => void;
}) {
  const { badge } = account.limits;
  const [justPaid] = useState(() => new URLSearchParams(window.location.search).get('addon') === 'success');
  if (account.level === 'none' || account.level === 'expired') return null;

  let body: preact.ComponentChildren;
  if (badge === 'removed') {
    body = <p class="db-sub">Your plan already leaves the VolgaChat badge off your chat — nothing to add.</p>;
  } else if (badge === 'shown') {
    body = (
      <p class="db-sub">
        Your chat shows "Powered by VolgaChat"{account.level === 'trial' ? ' during the free trial' : ''}. You can remove
        it for ${REMOVE_BADGE_ADDON.monthly}/mo on Starter, and Professional and Business leave it off. <a href="/pricing">See plans</a>
      </p>
    );
  } else if (account.removeBadgeAddon) {
    body = <ActiveRemoveBadgeCard cancelsAt={cancelsAt} onChange={onChange} />;
  } else if (paymentFailed) {
    // Still subscribed, so no second "Remove branding" checkout: the fix
    // is the card, in the payment portal.
    body = (
      <div class="db-addon">
        <div class="db-addon-text">
          <strong>{REMOVE_BADGE_ADDON.name}</strong>
          <span>
            Payment failed — your last ${REMOVE_BADGE_ADDON.monthly} payment didn't go through, so the VolgaChat badge is showing again.
            Update your card with Manage billing above, and the badge disappears as soon as the payment succeeds.
          </span>
        </div>
      </div>
    );
  } else {
    body = <RemoveBadgeCard active={false} checkoutOpen={checkoutOpen} justPaid={justPaid} />;
  }

  return (
    <>
      <h2 class="db-usage-h2">Add-ons</h2>
      {body}
    </>
  );
}

/** Self-serve tiers from cheapest to dearest — what decides whether a
 * change is an upgrade (charged now) or a downgrade (credited). */
const TIER_ORDER = ['basic', 'professional', 'enterprise'];

/**
 * Confirming a plan change for a customer who already pays. The pricing
 * page's Subscribe sends existing subscribers here (?change=<plan>&cycle=…)
 * instead of to a second checkout, so they never hold two subscriptions:
 * this moves the one they have (POST /billing/change-plan, prorated).
 */
/** How often, and for how long, the change-plan wait checks for Dodo's
 * confirmation before saying it's taking longer than usual. */
const CONFIRM_POLL_MS = 3000;
const CONFIRM_GIVE_UP_MS = 2 * 60 * 1000;
/** How often the wait also has the server ask Dodo itself whether the
 * change went through — every 10s at first, once a minute once it's slow.
 * The webhook that normally confirms it may be late, or never come. */
const CONFIRM_WITH_DODO_MS = 10_000;
const CONFIRM_WITH_DODO_SLOW_MS = 60_000;
/** How often a change blocked by a payment still settling looks again. */
const BLOCKED_RECHECK_MS = 30_000;

function fmtDateTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "UPI", "Card ending 4242" — how Dodo says it was paid, in plain words. */
function methodLabel(method: string | null | undefined, lastFour?: string | null): string | null {
  if (!method) return lastFour ? `Card ending ${lastFour}` : null;
  const m = method.toLowerCase();
  if (m === 'upi' || m.includes('upi')) return 'UPI';
  if (m === 'card' || m === 'credit' || m === 'debit') return lastFour ? `Card ending ${lastFour}` : 'Card';
  if (m.includes('bank')) return 'Bank transfer';
  return method.replace(/_/g, ' ');
}

const CHARGE_LABEL: Record<string, string> = {
  new: 'First payment',
  renewal: 'Renewal',
  plan_change: 'Plan change',
  on_demand: 'Charge',
};

/** Paid by UPI, a bank debit or similar — settled by the bank later, not at once. */
function isBankSettled(method: string | null): boolean {
  return /upi|bank|ach|sepa|debit/i.test(method ?? '');
}

/** What a payment's state means for the customer, and what (if anything)
 * they can do — worded per Dodo status, since "processing" by UPI and
 * "waiting for you to approve it" ask very different things of them. */
function paymentExplanation(p: PaymentStatus): string {
  if (p.state === 'failed') {
    return p.chargeType === 'plan_change'
      ? "You weren't charged, and your plan stays as it was. You can try the change again — with a different payment method from Manage billing if this one keeps failing."
      : "You weren't charged for it. Use Manage billing to update your payment method, or try again.";
  }
  if (p.state === 'succeeded') {
    return p.chargeType === 'plan_change' ? 'Paid. Dodo Payments is now moving your subscription to the new plan.' : 'Paid.';
  }
  switch (p.status) {
    case 'processing':
      return isBankSettled(p.method)
        ? 'Your bank confirms UPI and bank payments in its own time — usually within minutes, but it can take up to a day or two. Nothing more is needed from you, and this page updates once Dodo Payments hears back. Please don’t pay again in the meantime.'
        : 'Dodo Payments is waiting for your bank to confirm it. This usually takes a few minutes. Nothing more is needed from you.';
    case 'requires_customer_action':
      return 'Your bank is waiting for you to approve this payment. Check your UPI app or banking app for a payment request, or a message from your bank.';
    case 'requires_payment_method':
      return 'This payment needs a working payment method. Use Manage billing to update your card or UPI details.';
    default:
      return 'Dodo Payments is holding this payment for a check on their side. If it hasn’t moved after a day, email support@volgachat.com and quote the payment reference below.';
  }
}

/**
 * A payment explained in full — the amount, how and when it was paid, what
 * Dodo Payments says it's doing now and, for a failed one, Dodo's reason.
 * Shown wherever a payment is holding something up, so the customer reads
 * the actual reason rather than a "try again later" that doesn't help.
 */
export function PaymentStatusCard({ payment }: { payment: PaymentStatus }) {
  const tone = payment.state === 'failed' ? ' db-plan-danger' : payment.state === 'succeeded' ? ' db-plan-success' : ' db-plan-warn';
  const title =
    payment.state === 'failed'
      ? "This payment didn't go through"
      : payment.state === 'succeeded'
        ? 'Payment received'
        : 'Waiting for a payment to go through';
  const method = methodLabel(payment.method, payment.cardLastFour);
  return (
    <div class={`db-plan-banner db-pay-status${tone}`} role="status" aria-live="polite">
      <div class="db-plan-text">
        <strong>{title}</strong>
        <span>{paymentExplanation(payment)}</span>
        <dl class="db-pay-facts">
          <div>
            <dt>Amount</dt>
            <dd>{fmtMoney(payment.amount, payment.currency)}</dd>
          </div>
          {method && (
            <div>
              <dt>Paid by</dt>
              <dd>{method}</dd>
            </div>
          )}
          {payment.chargeType && CHARGE_LABEL[payment.chargeType] && (
            <div>
              <dt>For</dt>
              <dd>{CHARGE_LABEL[payment.chargeType]}</dd>
            </div>
          )}
          <div>
            <dt>Started</dt>
            <dd>{fmtDateTime(payment.createdAt)}</dd>
          </div>
          {payment.updatedAt && payment.updatedAt !== payment.createdAt && (
            <div>
              <dt>Last update</dt>
              <dd>{fmtDateTime(payment.updatedAt)}</dd>
            </div>
          )}
          <div>
            <dt>Status at Dodo Payments</dt>
            <dd>
              <span class={`db-badge ${PAYMENT_TONE[payment.status] ?? 'db-badge-pending'}`}>{payment.status.replace(/_/g, ' ')}</span>
            </dd>
          </div>
          {payment.state === 'failed' && (
            <div class="db-pay-reason">
              <dt>Reason</dt>
              <dd>
                {payment.errorMessage ?? "Dodo Payments didn't give a reason."}
                {payment.errorCode ? <span class="db-ref-id">{payment.errorCode}</span> : null}
              </dd>
            </div>
          )}
          <div>
            <dt>Payment reference</dt>
            <dd>
              <span class="db-ref-id">{payment.paymentId}</span>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

/**
 * The wait after "Change to X": the change is requested, and the plan
 * switches when Dodo's webhook lands (subscription.plan_changed) — or when
 * the server, asked every so often, finds Dodo has already moved the
 * subscription. It checks the account every few seconds and shows where it
 * is — three steps, the middle one moving — then confirms on screen, or
 * after two minutes says plainly that it's slow instead of spinning for ever.
 */
function PlanChangeProgress({
  name,
  phase,
  payment,
  uncertain,
}: {
  name: string;
  phase: 'confirming' | 'confirmed' | 'slow';
  /** The payment behind the change, once Dodo shows one. */
  payment: PaymentStatus | null;
  /** Dodo didn't answer the request in time: it may not have happened. */
  uncertain: boolean;
}) {
  // Each step is worded for its state: what's happening while it happens,
  // what happened once it's done.
  const confirmed = phase === 'confirmed';
  const waitingOnPayment = !confirmed && payment?.state === 'waiting';
  const via = payment ? methodLabel(payment.method, payment.cardLastFour) : null;
  const steps: Array<{ label: string; state: 'done' | 'active' | 'todo' }> = [
    { label: 'Change requested', state: 'done' },
    {
      label: confirmed
        ? 'Confirmed by Dodo Payments'
        : waitingOnPayment
          ? via
            ? `Waiting for your ${via} payment`
            : 'Waiting for your payment'
          : 'Confirming with Dodo Payments',
      state: confirmed ? 'done' : 'active',
    },
    { label: confirmed ? `Your ${name} plan is live` : `${name} plan goes live`, state: confirmed ? 'done' : 'todo' },
  ];
  return (
    <div class={`db-plan-banner db-plan-progress${phase === 'confirmed' ? ' db-plan-success' : ''}`} role="status" aria-live="polite">
      <div class="db-plan-text">
        <strong>
          {phase === 'confirmed' ? `You're now on ${name}.` : phase === 'slow' ? `Still confirming your change to ${name}…` : `Changing your plan to ${name}…`}
        </strong>
        <span>
          {phase === 'confirmed'
            ? 'Your new plan and its limits apply from now on.'
            : waitingOnPayment
              ? `Your plan changes as soon as this payment goes through — the details are below. You can keep working, or leave this page and come back.`
              : uncertain
                ? "Dodo Payments didn't answer our request in time, so we're checking with them whether the change went through. Please don't try again until this finishes — it could charge you twice."
                : phase === 'slow'
                  ? "This is taking longer than usual. Nothing more is needed from you — the change will show here once Dodo Payments confirms it, or refresh the page in a few minutes."
                  : 'Dodo Payments is confirming it now. This usually takes a few seconds — you can keep working.'}
        </span>
        <ol class="db-progress-steps" aria-label="Plan change progress">
          {steps.map((s) => (
            <li key={s.label} class={`db-progress-step db-progress-${s.state}`}>
              <span class="db-progress-icon" aria-hidden="true">
                {s.state === 'done' ? '✓' : s.state === 'active' ? <span class="db-spinner db-spinner-sm" /> : ''}
              </span>
              {s.label}
              <span class="pc-sr-only">{s.state === 'done' ? ' — done' : s.state === 'active' ? ' — in progress' : ' — next'}</span>
            </li>
          ))}
        </ol>
        {phase !== 'confirmed' && <span class="db-progress-bar" aria-hidden="true" />}
      </div>
      {!confirmed && payment && <PaymentStatusCard payment={payment} />}
    </div>
  );
}

function ChangePlanPanel({
  account,
  plan,
  cycle,
  currentCycle,
  pending,
  initialPayment,
  onConfirmed,
}: {
  account: AccountInfo;
  plan: string;
  cycle: 'monthly' | 'yearly';
  /** How the current subscription is billed, from Dodo — null if unknown. */
  currentCycle: 'monthly' | 'yearly' | null;
  /** This change was already requested (before a refresh, say) and isn't
   * confirmed yet: show its progress, not a second "Change to X". */
  pending: boolean;
  /** The payment holding things up as the page loaded (BillingOverview's
   * paymentStatus): one still settling blocks the change until it's done. */
  initialPayment: PaymentStatus | null;
  /** The new plan is active: reload what the page shows about billing. */
  onConfirmed: () => void;
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'confirmed' | 'slow'>(() => (pending ? 'done' : 'idle'));
  const [payment, setPayment] = useState<PaymentStatus | null>(initialPayment);
  const [uncertain, setUncertain] = useState(false);
  const blocked = state === 'idle' && payment?.state === 'waiting';
  // Blocked by a payment still settling: look again every so often, and
  // offer the change as soon as it's settled — without a refresh.
  useEffect(() => {
    if (!blocked) return;
    let live = true;
    const timer = window.setInterval(async () => {
      const latest = await dashboardApi.getBilling().catch(() => null);
      if (live && latest) setPayment(latest.paymentStatus ?? null);
    }, BLOCKED_RECHECK_MS);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [blocked]);
  // While waiting: check the account until the new plan has been applied.
  // Nothing else on this page polls for a customer who already pays (the
  // top banner only waits on a first purchase), so without this the "shows
  // the new plan as soon as it's done" promise needed a refresh.
  const waitStarted = useRef(0);
  const lastDodoCheck = useRef(0);
  // Same plan on the other cycle: the plan already matches before Dodo has
  // done anything, so only its billing interval says the switch happened.
  // Decided once, from the plan the change started on — the account
  // refreshes to the new plan mid-check, which would otherwise flip this.
  const [switchingCycle] = useState(() => account.level === plan);
  useEffect(() => {
    if (state !== 'done' && state !== 'slow') return;
    if (!waitStarted.current) waitStarted.current = Date.now();
    let live = true;
    const tick = async () => {
      const every = Date.now() - waitStarted.current > CONFIRM_GIVE_UP_MS ? CONFIRM_WITH_DODO_SLOW_MS : CONFIRM_WITH_DODO_MS;
      if (Date.now() - lastDodoCheck.current >= every) {
        lastDodoCheck.current = Date.now();
        // Best effort: a failed check just leaves it to the next one (or
        // the webhook). The account is re-read below either way.
        const check = await dashboardApi.confirmPlanChange().catch(() => null);
        if (!live) return;
        if (check && check.result !== 'unavailable') setPayment(check.payment);
        if (check?.result === 'failed') {
          // Its payment failed and Dodo kept the old plan: show why, and
          // offer the change again.
          setUncertain(false);
          setState('idle');
          return;
        }
        if (check?.result === 'not_started') {
          setUncertain(false);
          setError(
            "Dodo Payments has no record of this change, so nothing was charged and your plan is unchanged. You can try again — if it fails the same way, email support@volgachat.com.",
          );
          setState('idle');
          return;
        }
      }
      const latest = await refreshAccount();
      let applied = latest?.level === plan;
      if (applied && switchingCycle) {
        const interval = (await dashboardApi.getBilling().catch(() => null))?.subscription?.paymentFrequencyInterval ?? '';
        applied = (cycle === 'yearly' ? /year/i : /month/i).test(interval);
      }
      if (!live) return;
      if (applied) {
        setState('confirmed');
        // The ?change= that opened this panel would reopen it on a refresh.
        window.history.replaceState({}, '', '/app/billing');
        onConfirmed();
      } else if (Date.now() - waitStarted.current > CONFIRM_GIVE_UP_MS) {
        setState('slow');
      }
    };
    const timer = window.setInterval(() => void tick(), CONFIRM_POLL_MS);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [state, plan, cycle, switchingCycle]);
  const [error, setError] = useState<string | null>(null);
  const target = PLANS.find((p) => p.id === plan);
  if (!target || target.price === null) return null;

  const name = target.name;
  const billedAs = cycle === 'yearly' ? `$${(target.yearlyTotal ?? 0).toLocaleString('en-US')} a year` : `$${priceFor(target, cycle)} a month`;
  const fromIdx = TIER_ORDER.indexOf(account.level);
  const toIdx = TIER_ORDER.indexOf(plan);
  const isDowngrade = fromIdx > -1 && toIdx > -1 && toIdx < fromIdx;
  // Moving to a plan that hides the badge anyway ends the add-on; one where
  // it's still removable (Starter) keeps it.
  const removableOnTarget = target.limits.some((l) => l.label === 'Branding' && l.value.startsWith('Removable'));
  const endsAddon = account.removeBadgeAddon && account.limits.badge === 'addon' && !removableOnTarget;

  async function confirm() {
    setState('busy');
    setError(null);
    try {
      const res = await dashboardApi.changePlan(plan, cycle);
      setUncertain(Boolean(res.uncertain));
      setPayment(null);
      setState('done');
    } catch (err) {
      // Refused because a payment is still settling: that payment, shown in
      // full below, says it better than any message could.
      const blocking = err instanceof ApiError ? (err.data.payment as PaymentStatus | undefined) : undefined;
      if (blocking) setPayment(blocking);
      else setError(err instanceof Error ? err.message : "We couldn't change your plan just now, and nothing was charged.");
      setState('idle');
    }
  }

  // Subscribe on the plan (and cycle) they already pay for lands here too.
  // Confirming it would ask Dodo to "change" to the same product and tell
  // them their billing date moves — so say they're on it instead.
  if (state === 'idle' && plan === account.level && (currentCycle === null || currentCycle === cycle)) {
    return (
      <div class="db-plan-banner" role="status">
        <div class="db-plan-text">
          <strong>You're already on {name}.</strong>
          <span>Nothing to change. Pick a different plan or billing cycle on the pricing page to switch.</span>
        </div>
      </div>
    );
  }

  if (state === 'done' || state === 'slow' || state === 'confirmed') {
    return <PlanChangeProgress name={name} phase={state === 'done' ? 'confirming' : state} payment={payment} uncertain={uncertain} />;
  }

  return (
    <div class="db-change-plan" role="region" aria-label="Confirm plan change">
      <h2 class="db-usage-h2">
        Change to {name} ({cycle})
      </h2>
      <p class="db-sub">
        {name} costs <strong>{billedAs}</strong>.{' '}
        {isDowngrade
          ? 'The unused part of your current plan is credited toward it.'
          : 'Today you pay only the difference for the rest of your current billing period, and your billing date moves to today.'}{' '}
        Your current subscription is changed — you're never billed for two plans.
      </p>
      {endsAddon && (
        <p class="db-sub">
          {name} already hides the VolgaChat badge, so your ${REMOVE_BADGE_ADDON.monthly}/mo Remove branding add-on is cancelled
          automatically and won't be charged again.
        </p>
      )}
      {blocked && (
        <p class="db-sub">
          <strong>You can change plan once this payment finishes.</strong> Dodo Payments doesn't allow a plan change while a payment on
          your subscription is still going through. This page checks again by itself.
        </p>
      )}
      {payment && payment.state !== 'succeeded' && <PaymentStatusCard payment={payment} />}
      <ErrorNote error={error} />
      <div class="db-bill-actions">
        <button type="button" class="site-btn site-btn-primary" onClick={confirm} disabled={state === 'busy' || blocked}>
          {state === 'busy' ? 'Changing…' : payment?.state === 'failed' ? `Try again — change to ${name}` : `Change to ${name}`}
        </button>
        <a href="/app/billing" class="site-btn">
          Keep my current plan
        </a>
      </div>
    </div>
  );
}

export function BillingPage() {
  const [data, setData] = useState<BillingOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);

  // Bumped when a plan change is confirmed, to show the new plan's details.
  const [reloads, setReloads] = useState(0);
  // The change that was just confirmed. Its ?change= is taken out of the
  // address once it's done (so a refresh doesn't reopen it), which would
  // otherwise also take down the panel before it could say "You're now on".
  const [confirmedChange, setConfirmedChange] = useState<{ plan: string; cycle: 'monthly' | 'yearly' } | null>(null);
  useEffect(() => {
    let live = true;
    dashboardApi
      .getBilling()
      .then((res) => {
        if (live) setData(res);
      })
      .catch((err) => {
        if (live) setError(err instanceof Error ? err.message : 'Could not load your billing details.');
      });
    return () => {
      live = false;
    };
  }, [reloads]);

  async function openPortal() {
    setPortalBusy(true);
    setError(null);
    try {
      const { url } = await dashboardApi.billingPortal();
      window.location.href = url; // a one-time link — leave for it immediately
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open the billing portal.');
      setPortalBusy(false);
    }
  }

  if (!data) {
    return (
      <PageShell current="billing">{error ? <ErrorNote error={error} /> : <Loading label="Loading your billing details…" />}</PageShell>
    );
  }

  const { account, subscription, payments } = data;
  const currentCycle = /year/i.test(subscription?.paymentFrequencyInterval ?? '')
    ? 'yearly'
    : /month/i.test(subscription?.paymentFrequencyInterval ?? '')
      ? 'monthly'
      : null;
  // ?change=<plan>&cycle=<cycle> — the pricing page's Subscribe for someone
  // who already pays (see ChangePlanPanel). Without one, a change still
  // waiting to be confirmed gets its progress shown all the same — the
  // customer coming back to check on it shouldn't find no sign of it. (Its
  // cycle only matters before it's requested, so any will do there.)
  const params = new URLSearchParams(window.location.search);
  const changeCycle = params.get('cycle');
  const changeTo =
    params.get('change') && (changeCycle === 'monthly' || changeCycle === 'yearly')
      ? { plan: params.get('change') as string, cycle: changeCycle as 'monthly' | 'yearly' }
      : (confirmedChange ?? (data.pendingPlanChange ? { plan: data.pendingPlanChange, cycle: currentCycle ?? 'monthly' } : null));
  const onPaidPlan = isPaidLevel(account.level);

  return (
    <PageShell current="billing">
      <h1>Billing</h1>
      <p class="db-sub">What you are on, what you pay for it, and every payment taken so far.</p>
      <ErrorNote error={error} />

      {changeTo && (
        <ChangePlanPanel
          account={account}
          plan={changeTo.plan}
          cycle={changeTo.cycle}
          currentCycle={currentCycle}
          pending={data.pendingPlanChange === changeTo.plan}
          initialPayment={data.paymentStatus ?? null}
          onConfirmed={() => {
            setConfirmedChange(changeTo);
            setReloads((n) => n + 1);
          }}
        />
      )}
      {/* Not changing plan, but a payment is still settling (or a change's
          payment failed): say so here too, not only inside a change. */}
      {!changeTo && data.paymentStatus && data.paymentStatus.state !== 'succeeded' && (
        <PaymentStatusCard payment={data.paymentStatus} />
      )}

      <PlanSummary account={account} />

      {subscription ? (
        <div class="db-bill-facts">
          <div class="db-bill-fact">
            <p class="db-bill-fact-label">You pay</p>
            <p class="db-bill-fact-value">
              {subscription.recurringAmount !== null && subscription.currency
                ? fmtMoney(subscription.recurringAmount, subscription.currency)
                : EM_DASH}
              {subscription.paymentFrequencyInterval ? (
                <span class="db-bill-fact-unit"> / {subscription.paymentFrequencyInterval.toLowerCase()}</span>
              ) : null}
            </p>
          </div>
          <div class="db-bill-fact">
            <p class="db-bill-fact-label">{subscription.cancelAtNextBillingDate ? 'Access ends' : 'Renews on'}</p>
            <p class="db-bill-fact-value">{fmtDate(subscription.nextBillingAt)}</p>
          </div>
          <div class="db-bill-fact">
            <p class="db-bill-fact-label">Status</p>
            <p class="db-bill-fact-value db-bill-status">{subscription.status.replace(/_/g, ' ')}</p>
          </div>
        </div>
      ) : account.level === 'trial' ? (
        <div class="db-bill-facts">
          <div class="db-bill-fact">
            <p class="db-bill-fact-label">Trial ends</p>
            <p class="db-bill-fact-value">{fmtDate(account.trialEndsAt)}</p>
          </div>
          <div class="db-bill-fact">
            <p class="db-bill-fact-label">You pay</p>
            <p class="db-bill-fact-value">Nothing yet</p>
          </div>
        </div>
      ) : null}

      {subscription?.cancelAtNextBillingDate && (
        <div class="db-plan-banner db-plan-warn" role="status">
          <div class="db-plan-text">
            <strong>Your subscription is set to end.</strong>
            <span>
              You keep {planLabel(account.level)} until {fmtDate(subscription.nextBillingAt)}. You can restart it any time from the
              billing portal.
            </span>
          </div>
        </div>
      )}

      <div class="db-bill-actions">
        {data.portalAvailable && (
          <button type="button" class="site-btn site-btn-primary" onClick={openPortal} disabled={portalBusy}>
            {portalBusy ? 'Opening…' : 'Manage billing'}
          </button>
        )}
        {/* Primary whenever it's the only action on the page — which is
            every trial account, since portalAvailable requires a Dodo
            customer that a trial never has. Secondary only when it's
            sitting next to Manage billing, so the two don't compete. */}
        <a href="/pricing" class={data.portalAvailable ? 'site-btn site-btn-secondary' : 'site-btn site-btn-primary'}>
          {onPaidPlan ? 'Change plan' : 'See plans'}
        </a>
      </div>
      {data.portalAvailable && (
        <p class="db-sub db-bill-portal-note">
          Update your card, download invoices or cancel your subscription in the secure payment portal.
        </p>
      )}

      <BillingReference reference={data.reference} />

      <Addons
        account={account}
        checkoutOpen={data.removeBadgeCheckout}
        cancelsAt={data.removeBadgeCancelsAt}
        paymentFailed={data.removeBadgePaymentFailed}
        onChange={(removeBadgeCancelsAt, next) => setData({ ...data, removeBadgeCancelsAt, account: { ...account, ...next } })}
      />

      <h2 class="db-usage-h2">Payment history</h2>
      {data.providerError ? (
        // An empty table would read as "you have never paid", which is a
        // far worse thing to tell someone than "we couldn't check".
        <ErrorNote error={`${data.providerError} Your plan and allowance above are unaffected.`} />
      ) : !data.providerConfigured ? (
        <p class="db-sub">Payment history is not available on this deployment.</p>
      ) : !data.hasDodoRecord ? (
        <p class="db-sub">{account.level === 'trial' ? 'No payments yet — you are on the free trial.' : 'No payments yet.'}</p>
      ) : payments.length === 0 ? (
        <p class="db-sub">No payments yet.</p>
      ) : (
        <div class="db-table-wrap">
          <table class="db-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>For</th>
                <th>Status</th>
                <th>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((row) => (
                <tr key={row.paymentId}>
                  <td>{fmtDate(row.createdAt)}</td>
                  <td>{fmtMoney(row.totalAmount, row.currency)}</td>
                  <td>
                    {[row.chargeType ? CHARGE_LABEL[row.chargeType] : null, methodLabel(row.paymentMethod, row.cardLastFour)]
                      .filter(Boolean)
                      .join(' · ') || EM_DASH}
                  </td>
                  <td>
                    <span class={`db-badge ${PAYMENT_TONE[row.status] ?? 'db-badge-pending'}`}>{row.status.replace(/_/g, ' ')}</span>
                  </td>
                  <td>
                    {row.invoiceUrl ? (
                      <a href={row.invoiceUrl} target="_blank" rel="noopener noreferrer">
                        View
                      </a>
                    ) : (
                      EM_DASH
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}
